<template>
    <DCard padding>
        <DAlert
            v-if="manualInvoiceUpload.status === 'open'"
            color="warning"
            class="d-flex align-items-center"
        >
            <DIcon
                name="alert"
                :size="24"
            />
            <div class="pr-2">
                {{ __('tools.invoice_uploads.preview.info_box') }}
            </div>
        </DAlert>
        <DRow>
            <DCol>
                <DTable
                    :items="items"
                    :fields="fields"
                    hover
                >
                    <template #actions="{item}">
                        <td>
                            <div style="display: flex; grid-gap: 10px">
                                <DButton
                                    icon="eye"
                                    mini
                                    @click="preview(item)"
                                />
                            </div>
                        </td>
                    </template>
                </DTable>
                <DPagination
                    v-if="paginationData.last_page > 1"
                    :active-page="paginationData.current_page"
                    :pages="paginationData.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
        <DFabs>
            <DFab
                id="btn-submit-invoices"
                icon="content-save"
                color="primary"
                :label="__('tools.invoice_uploads.preview.generate_invoices')"
                @click="generateInvoices"
            />
            <DFab
                v-if="manualInvoiceUpload.status === 'open'"
                id="btn-show-cancel-invoices"
                icon="close"
                color="danger"
                :label="__('tools.invoice_uploads.show.cancel')"
                @click="cancelInvoices"
            />
            <DFab
                id="btn-show-cancel-invoices"
                icon="close"
                color="secondary"
                @click="cancelGenerateInvoices"
            />
        </DFabs>
        <DModal
            :show="showConfirmationModal"
            :title="__('tools.invoice_uploads.preview.confirm_title')"
            :message="__('tools.invoice_uploads.preview.confirm_message')"
            is-confirm
            @close="showConfirmationModal = false"
            @confirm="createInvoices"
        />
        <DModal
            v-if="showDeleteConfirmationModal"
            v-model:show="showDeleteConfirmationModal"
            :title="__('tools.invoice_uploads.cancel.modal_title')"
            :message="__('tools.invoice_uploads.cancel.modal_text')"
            is-confirm
            is-error
            @close="closeDeleteModal"
            @confirm="cancelUpload"
        />
    </DCard>
    <DocumentPreSavePreview
        v-if="pdfSource !== null && numPages !== null"
        :title="__('customers.invoices.form.invoice_preview')"
        :pdf-source="pdfSource"
        :num-pages="numPages"
        :show-modal="showModal"
        @close="showModal = false"
    />
</template>

<script setup>
import { route } from '~@/Composables/route'
import { router } from '@inertiajs/vue3'
import { __ } from '~@/Composables/translation'
import { ref } from 'vue'

import pdf from 'vue3-pdf'
import DocumentPreSavePreview from '~@/Components/BillingAndAccounting/ManualInvoices/DocumentPreSavePreview.vue'
import NProgress from 'nprogress'

const props = defineProps({
    items: {
        type: Array,
        default: () => {
            return []
        }
    },
    paginationData: {
        type: Object,
        default: () => {
            return {}
        }
    },
    manualInvoiceUpload: {
        type: Object,
        default: () => {
            return {}
        }
    }
})

const fields = [
    { key: 'id', label: __('tools.invoice_uploads.preview.id'), _style: { minWidth: '10%' } },
    { key: 'customer', label: __('tools.invoice_uploads.preview.customer') },
    { key: 'date', label: __('tools.invoice_uploads.preview.date') },
    { key: 'due_date', label: __('tools.invoice_uploads.preview.due_date') },
    { key: 'price', label: __('tools.invoice_uploads.preview.total_amount') },
    { key: 'actions', label: __('tools.invoice_uploads.preview.preview'), _style: { minWidth: '5%' } }
]
const showModal = ref(false)
const numPages = ref(null)
const pdfSource = ref(null)
const showConfirmationModal = ref(false)
const errors = ref(null)

const cancelGenerateInvoices = () => router.get(route('tools.manual-invoice-uploads.show', { manualInvoiceUpload: props.manualInvoiceUpload.id }))
const generateInvoices = () => {
    showConfirmationModal.value = true
}
const createInvoices = () => router.get(route('tools.manual-invoice-uploads.store', { manualInvoiceUpload: props.manualInvoiceUpload.id }))
const changePage = (page) => {
    const query = {}
    if (page > 1) {
        query.page = page
    }

    router.get(route('tools.manual-invoice-uploads.preview', { manualInvoiceUpload: props.manualInvoiceUpload.id }), query)
}

const showDeleteConfirmationModal = ref(false)
const closeDeleteModal = () => {
    showDeleteConfirmationModal.value = false
}
const cancelUpload = () => {
    router.post(route('tools.manual-invoice-uploads.cancel', {
        manualInvoiceUpload: props.manualInvoiceUpload.id
    }))
}
const cancelInvoices = () => {
    showDeleteConfirmationModal.value = true
}

const preview = async (item) => {
    showModal.value = false
    numPages.value = null

    const response = await window.axios.post(route('tools.manual-invoice-uploads.pdf'), {
        customer: item.customer,
        manualInvoiceUpload: props.manualInvoiceUpload.id
    }, {
        responseType: 'blob'
    })

    if (response.status === 200) {
        NProgress.done()

        const blob = new Blob([response.data], { type: 'application/pdf' })
        pdfSource.value = URL.createObjectURL(blob)

        if (!pdfSource.value) {
            return
        }

        pdf.createLoadingTask(pdfSource.value)
            .promise
            .then(pdfInstance => {
                numPages.value = pdfInstance.numPages
                if (numPages.value !== null) {
                    showModal.value = true
                }
            })
            .catch(error => {
                errors.value = error.response.data.error
            })
    }
}

</script>

<style scoped lang="scss">

</style>
